import React, { useEffect, useRef, useState } from "react";
import { HiXMark } from "react-icons/hi2";

const ANIMATION_DURATION = 250;

// `tone` distingue sucesso de erro. O padrão mantém o laranja usado hoje, para
// não alterar a aparência de quem já usa o componente sem passar a prop.
const TONES = {
  success: { accent: "text-orange-500", bar: "bg-orange-500" },
  error: { accent: "text-red-600", bar: "bg-red-500" },
};

const AnimatedToast = ({
  open = false,
  message = "",
  duration = 2000,
  onClose,
  title = "Sucesso",
  tone = "success",
}) => {
  const toneStyle = TONES[tone] || TONES.success;
  const [progress, setProgress] = useState(0);
  const [isRendered, setIsRendered] = useState(open);
  const [animationState, setAnimationState] = useState(open ? "enter" : "exit");
  const intervalRef = useRef(null);
  const exitTimerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setIsRendered(true);
      requestAnimationFrame(() => {
        setAnimationState("enter");
      });
    } else if (isRendered) {
      setAnimationState("exit");
      exitTimerRef.current = window.setTimeout(() => {
        setIsRendered(false);
      }, ANIMATION_DURATION);
    }
    return () => {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    };
  }, [open, isRendered]);

  useEffect(() => {
    if (!open) {
      setProgress(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const intervalMs = 60;
    const step = Math.max(0.5, 100 / Math.max(1, duration / intervalMs));

    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        const next = Math.min(100, prev + step);
        if (next >= 100) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setTimeout(() => onClose?.(), ANIMATION_DURATION);
        }
        return next;
      });
    }, intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [open, duration, onClose]);

  if (!isRendered) return null;

  const handleClose = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    onClose?.();
  };

  const animationClasses =
    animationState === "enter"
      ? "translate-y-0 opacity-100"
      : "translate-y-6 opacity-0";

  return (
    <div
      className="fixed bottom-6 right-3 w-[340px] max-w-[calc(100vw-1.5rem)] sm:right-6"
      style={{ zIndex: 100000 }}
    >
      <div
        className={`flex flex-col gap-2 rounded-2xl bg-white dark:!bg-[#26232F] dark:border dark:border-solid dark:border-[#36323F] dark:text-[#F4F2F8] p-4 shadow-2xl transform transition-all duration-250 overflow-hidden ease-out ${animationClasses}`}
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 text-sm leading-snug">
            <p
              className={`text-[11px] uppercase tracking-[0.3em] ${toneStyle.accent}`}
            >
              {title}
            </p>
            <p className="text-sm font-medium ">{message}</p>
          </div>
          <button
            type="button"
            className="dark:hover:text-[#FF8A4C]"
            onClick={handleClose}
            aria-label="Fechar toast"
          >
            <HiXMark className="h-4 w-4" />
          </button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[5px] w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className={`h-full rounded-full transition-all duration-200 ${toneStyle.bar}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default AnimatedToast;
